import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Info, FileText, Shield, Bell } from 'lucide-react';
import {
  getAppVersion,
  getPlatform,
  openExternalUrl,
  getUserInfo,
  updatePushConsent,
  requestPushPermission,
  getPushPermissionStatus,
  isVersionBelow,
} from '../../services/api';
import './SettingsPage.css';

const TERMS_URL = 'https://impartial-ocicat-c23.notion.site/2314858496678040b33fcd7f872dd064?pvs=74';
const PRIVACY_URL = 'https://impartial-ocicat-c23.notion.site/172485849667804398d8cbfeafcebd27';

// 푸시 알림은 앱 버전 2.0.4부터 네이티브 브릿지가 지원됨 (그 미만 버전엔 해당 핸들러 없음)
const MIN_PUSH_APP_VERSION = '2.0.4';

const ToggleSwitch = ({ checked, disabled, onChange, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    className={`settings-toggle${checked ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}
    onClick={onChange}
  >
    <span className="settings-toggle-thumb" />
  </button>
);

const SettingsPage = () => {
  const navigate = useNavigate();
  const [webVersion, setWebVersion] = useState('');
  const [nativeVersion, setNativeVersion] = useState(null);

  const platform = getPlatform();
  const isNative = platform !== 'web';

  const [pushEnabled, setPushEnabled] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(null); // 'granted' | 'denied' | 'notDetermined'
  const [notifBusy, setNotifBusy] = useState(false);
  const [pushSupported, setPushSupported] = useState(false); // 앱 버전이 푸시 브릿지를 지원하는지 (버전 확인 전까지 false가 안전한 기본값)

  useEffect(() => {
    document.title = '설정 | 투자특공대';

    fetch('/version.json')
      .then((r) => r.json())
      .then((data) => setWebVersion(data.version || ''))
      .catch(() => {});

    if (isNative) {
      getAppVersion().then((v) => {
        setNativeVersion(v);
        const supported = !!v && !isVersionBelow(v, MIN_PUSH_APP_VERSION);
        setPushSupported(supported);
        if (!supported) return;

        getPushPermissionStatus()
          .then((status) => setPermissionStatus(status))
          .catch(() => {});

        getUserInfo()
          .then((info) => setPushEnabled(!!info?.pushAgreeYn))
          .catch(() => {});
      });
    }

    return () => { document.title = '투자특공대'; };
  }, [isNative]);

  // 전체 푸시 토글: 켤 때 권한 요청(승인 시 네이티브가 FCM 토큰을 자체 등록) → 동의 상태 저장 (실패 시 롤백)
  const handleMasterToggle = async () => {
    if (!isNative || !pushSupported || notifBusy) return;

    const turningOn = !pushEnabled;

    setNotifBusy(true);
    if (turningOn && permissionStatus !== 'granted') {
      const granted = await requestPushPermission();
      setPermissionStatus(granted ? 'granted' : 'denied');
      if (!granted) {
        setNotifBusy(false);
        return; // 권한 미승인 시 활성화하지 않음
      }
    }

    setPushEnabled(turningOn);
    const result = await updatePushConsent(turningOn);
    if (result === null) {
      setPushEnabled(!turningOn); // 저장 실패 시 롤백
    }
    setNotifBusy(false);
  };

  const permissionDenied = isNative && permissionStatus === 'denied';

  return (
    <div className="settings-page-container">
      <div className="far-header">
        <div className="far-header-left">
          <span className="far-header-report-type">SETTINGS</span>
          <div className="far-header-title-row">
            <span className="far-header-title">설정</span>
          </div>
        </div>
      </div>

      <div className="settings-page-body">
        <div className="settings-group">
          <h2 className="settings-group-label">알림 설정</h2>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-row-icon">
                <Bell size={18} />
              </div>
              <div className="settings-row-content">
                <span className="settings-row-label">푸시 알림</span>
                <span className="settings-row-desc">AI 리포트, 관심종목 소식 등을 알려드려요</span>
              </div>
              <ToggleSwitch
                ariaLabel="전체 푸시 알림"
                checked={pushEnabled}
                disabled={!isNative || !pushSupported || notifBusy}
                onChange={handleMasterToggle}
              />
            </div>
          </div>

          {!isNative && (
            <p className="settings-group-note">알림 기능은 앱에서만 사용할 수 있어요.</p>
          )}
          {isNative && !pushSupported && (
            <p className="settings-group-note">최신 버전으로 업데이트하면 사용할 수 있어요.</p>
          )}
          {permissionDenied && (
            <p className="settings-group-note settings-group-note-warn">
              기기 설정에서 알림 권한을 허용해야 알림을 받을 수 있어요.
            </p>
          )}
        </div>

        <div className="settings-group">
          <h2 className="settings-group-label">앱 정보</h2>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-row-icon">
                <Info size={18} />
              </div>
              <div className="settings-row-content">
                <span className="settings-row-label">웹 버전</span>
              </div>
              <span className="settings-row-value">{webVersion || '-'}</span>
            </div>
            {nativeVersion !== null && (
              <div className="settings-row">
                <div className="settings-row-icon">
                  <Info size={18} />
                </div>
                <div className="settings-row-content">
                  <span className="settings-row-label">앱 버전</span>
                </div>
                <span className="settings-row-value">{nativeVersion || '-'}</span>
              </div>
            )}
          </div>
        </div>

        {/* <div className="settings-group">
          <h2 className="settings-group-label">법적 고지</h2>
          <div className="settings-card">
            <button
              className="settings-row settings-row-link"
              onClick={() => openExternalUrl(TERMS_URL)}
            >
              <div className="settings-row-icon">
                <FileText size={18} />
              </div>
              <div className="settings-row-content">
                <span className="settings-row-label">이용약관</span>
              </div>
              <ChevronRight size={18} className="settings-chevron" />
            </button>

            <button
              className="settings-row settings-row-link"
              onClick={() => openExternalUrl(PRIVACY_URL)}
            >
              <div className="settings-row-icon">
                <Shield size={18} />
              </div>
              <div className="settings-row-content">
                <span className="settings-row-label">개인정보처리방침</span>
              </div>
              <ChevronRight size={18} className="settings-chevron" />
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default SettingsPage;
